/**
 * VmLifecycleBanner — the global strip for workstation idle-lifecycle states
 * (idle-lifecycle Phase 4, 2026-08-18; the banner owed to the cleanup-sweep
 * plan §4.3). Registers with GlobalBannerCenter (never stacks its own strip
 * inside the provider) with id `vm-lifecycle:{accountId}`.
 *
 * STEALTH posture (owner directive 2026-08-18 late evening): the lifecycle
 * never advertises itself. This strip surfaces ONLY the 'waking' state —
 * live progress feedback while a wake the user (or their turn) requested is
 * in flight. Pre-sleep warnings and app-wide "asleep" strips are gone: a
 * sleeping workstation is invisible until the user actually needs it, and
 * then the /desktop asleep card (wake button + gentle copy) or the in-chat
 * waking reply covers it. Billing sleeps render NOTHING here either — the
 * billingLifecycle dunning banners/notifications own that journey.
 *
 * ── TRUTHFULNESS (owner report 2026-08-31) ─────────────────────────────────
 * The reported defect: an established account whose desktop was demonstrably
 * live and working still read "waking your workstation — ready in about a
 * minute." Two independent causes, both fixed here, because either alone
 * reproduces it:
 *
 *  1. NO FRESHNESS BOUND. `lifecycleState: 'waking'` has exactly ONE clearing
 *     writer in the codebase (functions/src/prest0nVm/bootStatus.ts), and it
 *     only runs when something actively polls the boot-status probe. A wake
 *     whose promotion never ran — the poll stopped, the caller navigated
 *     away, the function errored — leaves the field at 'waking' FOREVER, and
 *     this banner replayed that stale field as live progress indefinitely.
 *     The server already knows better: it stamps the wake clock
 *     (`wakeRequestedAt`, else `lifecycleUpdatedAt`) on the very doc read
 *     here, and calls a wake past WORKSTATION_STALL_MINUTES stalled. So the
 *     banner now reads that same clock and simply stops claiming a wake it
 *     can no longer vouch for. Past the bound the /desktop card — which has
 *     the server's authoritative verdict and a retry — owns the story; a
 *     top strip promising "about a minute" does not.
 *
 *  2. THE WRONG WORKSPACE. accountId was resolved ONCE, from the signed-in
 *     uid, in an effect keyed only on that uid. Switching workspace does not
 *     change the uid, so the listener stayed pointed at the PREVIOUS
 *     account's vm doc: a wake in one workspace showed as a banner while the
 *     user sat in another whose desktop was perfectly healthy. Resolution is
 *     now a LIVE subscription to the acting-account back-pointer (the same
 *     doc, and the same reader, the wallet display uses), so the banner
 *     always speaks about the workspace the user is actually in.
 *
 * Between them these also settle the "old vs newly onboarded accounts" half
 * of the directive: a legacy doc that predates the lifecycle fields carries
 * no wake clock, and an unclocked 'waking' is treated as untrustworthy
 * rather than as a live wake — so old accounts stop inheriting a phantom
 * banner, while a genuine fresh wake on any account still shows normally.
 *
 * Live via onSnapshot on accounts/{accountId}/vm/main (account members have
 * read access per firestore.rules). Dismissal is session-scoped PER STATE so
 * a new lifecycle state re-surfaces the strip.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { getApp } from 'firebase/app';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { resolveAccount } from '../../services/prest0nRuntime/accountResolver';
import { ACCOUNT_FIRESTORE_PREFIX } from '../../services/prest0nRuntime/constants';
import { onAccountMembershipAccountIdSnapshot } from '../../repositories/walletRepository';
import { WORKSTATION_WAKE_ESTIMATE } from '../onboarding/JourneyStepper';
import { useRegisterBanner, type BannerDescriptor } from './GlobalBannerCenter';
import { useBannerSurface } from './bannerSurface';
import { isWakeBannerEligible, isPastWakeEstimate } from './vmWakeBannerPolicy';

type BannerState = 'waking' | null;

// The RULE lives in vmWakeBannerPolicy.ts (pure, testable without firebase);
// this module is the wiring. Re-exported so callers/tests have one import.
export {
  WORKSTATION_WAKE_STALE_MINUTES,
  WORKSTATION_WAKE_ESTIMATE_GRACE_MINUTES,
  wakeStartedAtMs,
  isWakeBannerEligible,
  isPastWakeEstimate,
} from './vmWakeBannerPolicy';

export default function VmLifecycleBanner(): React.ReactElement | null {
  const { isAuthenticated, user } = useAuth();
  const onBannerSurface = useBannerSurface();
  const [accountId, setAccountId] = useState<string | null>(null);
  const [vm, setVm] = useState<Record<string, any> | null>(null);
  const [dismissedKey, setDismissedKey] = useState<string | null>(() => {
    try { return sessionStorage.getItem('prest0_vm_lifecycle_banner_dismissed'); } catch { return null; }
  });

  // Acting-account resolution, LIVE (2026-08-31): a workspace switch does not
  // change the uid, so the old one-shot resolve left this listener pinned to
  // the previous workspace's vm doc. Subscribing to the back-pointer keeps
  // the banner about the workspace the user is actually in. The one-shot
  // resolver remains the fallback for accounts with no back-pointer doc.
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated || !user?.id) { setAccountId(null); return; }
    const uid = user.id;

    const fallback = () => {
      void (async () => {
        try {
          const resolution = await resolveAccount(uid);
          if (!cancelled) setAccountId(resolution?.accountId ?? uid);
        } catch {
          if (!cancelled) setAccountId(uid);
        }
      })();
    };

    let unsub: (() => void) | undefined;
    try {
      unsub = onAccountMembershipAccountIdSnapshot(
        uid,
        (backPointerAccountId) => {
          if (cancelled) return;
          if (backPointerAccountId) setAccountId(backPointerAccountId);
          else fallback();
        },
        () => { if (!cancelled) fallback(); },
      );
    } catch {
      fallback();
    }

    return () => { cancelled = true; unsub?.(); };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!accountId) { setVm(null); return; }
    // Reset on account change so a previous workspace's wake never lingers
    // for a frame against the new one.
    setVm(null);
    const db = getFirestore(getApp());
    const unsub = onSnapshot(
      doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'vm', 'main'),
      (snap) => setVm(snap.exists() ? (snap.data() as Record<string, any>) : null),
      () => setVm(null),
    );
    return unsub;
  }, [accountId]);

  // The staleness bound is a function of elapsed time, so re-evaluate on a
  // timer as well as on snapshot: a wake that goes stale while the user is
  // sitting on the page must retire itself without needing a doc write.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const wakingNow = typeof vm?.lifecycleState === 'string' && vm.lifecycleState === 'waking';
  useEffect(() => {
    if (!wakingNow) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
    setNowMs(Date.now());
    return () => window.clearInterval(id);
  }, [wakingNow, vm?.wakeRequestedAt, vm?.lifecycleUpdatedAt]);

  const state: BannerState = useMemo(
    () => (isWakeBannerEligible(vm, nowMs) ? 'waking' : null),
    [vm, nowMs],
  );

  // Past the estimate the wake is still believed, but the copy stops quoting
  // a number it has already missed.
  const pastEstimate = isPastWakeEstimate(vm, nowMs);

  const stateKey = accountId && state ? `${accountId}:${state}` : null;

  const descriptor: BannerDescriptor | null = useMemo(() => {
    if (!onBannerSurface) return null;
    if (!accountId || !state || !stateKey || dismissedKey === stateKey) return null;
    const dismiss = () => {
      try { sessionStorage.setItem('prest0_vm_lifecycle_banner_dismissed', stateKey); } catch { /* noop */ }
      setDismissedKey(stateKey);
    };
    return {
      id: `vm-lifecycle:${accountId}`,
      tone: 'info',
      dismissible: true,
      onDismiss: dismiss,
      content: (
        <span>
          {pastEstimate
            ? 'waking your workstation — this one is taking a little longer.'
            : `waking your workstation — ready in ${WORKSTATION_WAKE_ESTIMATE}.`}
        </span>
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, state, stateKey, dismissedKey, pastEstimate, onBannerSurface]);

  useRegisterBanner(descriptor);
  return null;
}
